import { APP_AREAS } from './areas'
import { findMatchingOverride } from '../helpers/jsonConfig'

import { AppAreaConfig, DomainsConfig, DomainMatch, OverridesParams, AppArea } from './@types'
import { isAppAreaConfig, generateDomainConfig } from './funnelUtils'
import experimentsConfig from './experiments'

const mainFunnel = APP_AREAS.find((a) => a.appArea === 'main')
const assessmentFunnel = APP_AREAS.find((a) => a.appArea === 'assessment')
const workbooksFunnel = APP_AREAS.find((a) => a.appArea === 'workbooks')
const mainActivityFunnel = APP_AREAS.find((a) => a.appArea === 'main-activity')

const BAU_FUNNEL_PATHNAMES = [
	'/',
	'/f',
	'/f-al',
	'/m',
	'/assessment',
	'/workbooks',
	'/mediterranean-diet-f',
	'/keto-diet-f',
	'/cortisol-diet-f',
	'/menopause-diet-f',
	'/together',
	'/call',
	'/carnivore-diet-m',
	'/barre-f',
	'/glp-f',
	'/glp-1',
	'/water-balance-f',
]

const allFunnels = generateDomainConfig(APP_AREAS, {
	'/': mainFunnel,
	'/assessment': assessmentFunnel,
	'/workbooks': workbooksFunnel,
})

// BAU funnels - только указанные pathnames
const bauFunnels = Object.fromEntries(
	BAU_FUNNEL_PATHNAMES.filter((key) => key in allFunnels).map((key) => [key, allFunnels[key]]),
)

// Activity funnels - все кроме BAU
const activityFunnels = Object.fromEntries(
	Object.entries(allFunnels).filter(([key]) => !BAU_FUNNEL_PATHNAMES.includes(key)),
)

const config: DomainsConfig = {
	'simple.life': {
		experiments: true,
		pathnames: allFunnels,
	},
	'simple-life-app.com': {
		experiments: true,
		pathnames: bauFunnels,
	},
	'activity.simple-life-app.com': {
		experiments: true,
		pathnames: {
			'/': mainActivityFunnel,
			...activityFunnels,
		},
	},
	'simple-pilates.life': {
		experiments: true,
		pathnames: {
			'/': allFunnels['/pilates-f'],
			'/pilates-f': allFunnels['/pilates-f'],
			'/chair-workouts-m': allFunnels['/chair-workouts-m'],
		},
	},
	'simple-yoga.life': {
		experiments: true,
		pathnames: {
			'/': allFunnels['/yoga'],
			'/yoga-f': allFunnels['/yoga-f'],
		},
	},
	'simple-walking.life': {
		experiments: true,
		pathnames: {
			'/': allFunnels['/walking'],
			'/walking-f': allFunnels['/walking-f'],
			'/walking-m': allFunnels['/walking-m'],
		},
	},
	'*.simple.life': {
		experiments: true,
		pathnames: allFunnels,
	},
}

/**
 * Array of domain configurations with their corresponding regex patterns.
 * Used for matching hostnames and retrieving domain-specific settings.
 *
 * @type {DomainMatch[]}
 *
 * @property {Object} domainConfig - Configuration for the domain
 * @property {boolean} domainConfig.experiments - Whether experiments are enabled for this domain
 * @property {Object} domainConfig.pathnames - Object containing path configurations and service prefixes
 * @property {RegExp} regex - Regular expression for matching the domain pattern
 *
 * @example
 * // Example of a domain match object:
 * {
 *   domainConfig: {
 *     experiments: true,
 *     pathnames: { '/': {...}, '/account': {...} }
 *   },
 *   regex: /^simple-life-app\.com$/
 * }
 */
export const DOMAINS_FROM_CONFIG = Object.entries(config).reduce<DomainMatch[]>((result, [domain, domainConfig]) => {
	return [
		...result,
		{
			domainConfig: { ...domainConfig, pathnames: { ...domainConfig.pathnames } },
			regex: new RegExp(`^${domain.replace(/^\*/, '.*')}$`),
		},
	]
}, [])

/**
 * Array of tuples containing URL path prefixes and their corresponding regex patterns.
 * Used for validating and matching URL paths across different domains.
 * Includes both domain-specific pathnames and service prefixes (like '/account', '/redirect').
 *
 * @type {[string, RegExp][]}
 *
 * @example
 * // Returns array like:
 * [
 *   ['/account', /^\/account(?:\/[^-][^/]*)*\/?$/],
 *   ['/redirect', /^\/redirect(?:\/[^-][^/]*)*\/?$/],
 *   ['/female', /^\/female(?:\/[^-][^/]*)*\/?$/]
 * ]
 */
export const DOMAINS_PREFIXES = [
	...new Set(
		Object.values(config)
			.map((domainConfig) => Object.keys(domainConfig.pathnames))
			.flat()
			.filter((prefix) => prefix !== '/'),
	),
].map((prefix) => [prefix, new RegExp(`^${prefix}(?:/[^-][^/]*)*/?$`)])

/**
 * Retrieves specific configuration part from active experiments for given app area.
 *
 * @param {AppArea} appArea - The application area to get experiments for (e.g., 'female', 'activity-female-yoga')
 * @param {'landing' | 'funnel' | 'prePaywall' | 'paywall' | 'upsell'} part - The configuration part to retrieve
 * @param {Function} $absmartly - A/B Smartly instance
 *
 * @returns {Object|null} Configuration part from the active experiment or null if no active experiments found
 *
 * @example
 * // For funnel configuration:
 * getAppAreaExperimentPart('female', 'funnel', this.$absmartly)
 */
// eslint-disable-next-line import/no-unused-modules
export const getAppAreaExperimentPart = (
	appArea: AppArea,
	part: 'landing' | 'funnel' | 'prePaywall' | 'paywall' | 'upsell',
	$absmartly: any, // eslint-disable-line @typescript-eslint/no-explicit-any
) => {
	const appAreaExperiments = experimentsConfig[appArea]

	if (typeof appAreaExperiments !== 'object' || Object.keys(appAreaExperiments).length === 0) {
		return null
	}

	for (const experimentName of Object.keys(appAreaExperiments)) {
		const { assigned, variant } = $absmartly.assign(experimentName)

		// TODO experiments intersection handler

		if (!assigned) {
			continue
		}

		let variantIndex = 0

		if (variant > 0) {
			variantIndex = variant - 1
		}

		const experimentVariantConfig = appAreaExperiments[experimentName][variantIndex]
		const experimentAreaConfig = experimentVariantConfig?.[appArea]

		if (!experimentAreaConfig) {
			console.error('Incorrect experiment config for this appArea')
			continue
		}

		if (experimentAreaConfig[part]) {
			if (part === 'funnel') {
				// For funnel experiments return the experimental config even for variant A
				// Don't call $absmartly.treatment() due to Experimental Flow™
				return experimentAreaConfig[part]
			}

			if ($absmartly.treatment(experimentName) > 0) {
				// For other experiment parts call $absmartly.treatment() to exposure the user to the experiment
				return experimentAreaConfig[part]
			}
		}
	}

	return null
}

/**
 * Applies overrides to a specific part of the area configuration (paywall, upsell, funnel, or upgrade).
 * Used to customize configuration based on country
 *
 * @param {AppAreaConfig | { appArea: string }} areaConfig - Base configuration for the area
 * @param {'paywall' | 'upsell' | 'funnel' | 'upgrade'} configKey - The configuration key to apply overrides to
 * @param {OverridesParams} params - Parameters for override conditions
 * @param {string} params.country - Country code to check overrides against
 *
 * @returns {AppAreaConfig} Modified or original area configuration
 */
const applyConfigOverrides = (
	areaConfig: AppAreaConfig | { appArea: string },
	configKey: 'paywall' | 'upsell' | 'funnel' | 'upgrade',
	{ country }: OverridesParams,
): AppAreaConfig => {
	if (isAppAreaConfig(areaConfig)) {
		const configSection = areaConfig[configKey]

		if (configSection && Array.isArray(configSection.overrides)) {
			const matchingOverride = findMatchingOverride(configSection.overrides, { country })

			if (matchingOverride && typeof matchingOverride === 'object' && !Array.isArray(matchingOverride)) {
				// Хотим записать только свойства из override, без matcher
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { matcher: _, ...overrideData } = matchingOverride
				return {
					...areaConfig,
					[configKey]: {
						...configSection,
						...overrideData,
					},
				}
			}
		}
	}

	// eslint-disable-next-line no-restricted-syntax
	return areaConfig as AppAreaConfig
}

/**
 * Applies funnel experiment configuration to the base area configuration if an active experiment exists.
 *
 * @param {AppAreaConfig | { appArea: string }} areaConfig - Base configuration for the area
 * @param {Function} $absmartly - A/B Smartly instance
 *
 * @returns {AppAreaConfig} Configuration with applied funnel experiment or original configuration
 */
const applyFunnelExperiments = (areaConfig: AppAreaConfig | { appArea: string }, $absmartly): AppAreaConfig => {
	// eslint-disable-next-line no-restricted-syntax
	const funnelExperiment = getAppAreaExperimentPart(areaConfig.appArea as AppArea, 'funnel', $absmartly)

	if (isAppAreaConfig(areaConfig) && funnelExperiment) {
		return { ...areaConfig, funnel: { ...areaConfig.funnel, ...funnelExperiment } }
	}

	// eslint-disable-next-line no-restricted-syntax
	return areaConfig as AppAreaConfig
}

/**
 * Defines a funnel configuration based on either app area or initial path.
 * If appArea is provided, finds configuration by matching app area.
 * Otherwise, uses initialPath to find configuration in domain's pathnames.
 *
 * @param {Object} params - Parameters for funnel definition
 * @param {string|null} params.appArea - App area identifier (e.g., 'female', 'activity-female-yoga').
 *                                      Required if initialPath is not provided
 * @param {string|null} params.initialPath - URL path to match against domain's pathname configurations.
 *                                          Required if appArea is not provided
 * @param {string} params.country - Country code for applying country-specific overrides
 * @param {string} params.domain - Domain name to match against domain configurations
 * @param {Function} $absmartly - A/B Smartly instance
 *
 * @returns {AppAreaConfig} Funnel configuration with applied overrides and experiments
 *
 */
// eslint-disable-next-line import/no-unused-modules
export function defineFunnelByParams(params, $absmartly) {
	const { appArea, country, domain, initialPath } = params
	let currentAreaConfig

	if (appArea) {
		currentAreaConfig = DOMAINS_FROM_CONFIG.map((item) => Object.values(item.domainConfig.pathnames))
			.flat()
			.find((v) => v.appArea === appArea)
	} else if (initialPath) {
		const domainConfig = DOMAINS_FROM_CONFIG.find((item) => item.regex.test(domain))?.domainConfig

		currentAreaConfig = domainConfig?.pathnames[initialPath] || domainConfig?.pathnames['/']
	}

	if (!currentAreaConfig) {
		import('@/modules/Sentry.js').then((Sentry) => {
			Sentry.default.withScope((scope) => {
				scope.setTags({
					funnelTypeError: true,
				})
				scope.setExtras(params)
				scope.captureMessage(`No funnel found for this ${appArea ? 'app area' : 'domain'}`)
			})
		})

		currentAreaConfig = getDefaultAppAreaConfigByHostname(domain)
	}

	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'funnel', { country })
	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'paywall', { country })
	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'upsell', { country })
	currentAreaConfig = applyConfigOverrides(currentAreaConfig, 'upgrade', { country })

	if ($absmartly) {
		currentAreaConfig = applyFunnelExperiments(currentAreaConfig, $absmartly)
	}

	return currentAreaConfig
}

export const getDefaultAppAreaConfigByHostname = (hostname: string) => {
	const domainConfig = DOMAINS_FROM_CONFIG.find((item) => item.regex.test(hostname))?.domainConfig

	if (!domainConfig) {
		return allFunnels['/']
	}

	return domainConfig.pathnames['/']
}

export const getCrmRetargetingRouteNameBySlug = (hostname: string, slug: string, defaultRouteName: string): string => {
	const domainConfig = DOMAINS_FROM_CONFIG.find((item) => item.regex.test(hostname))?.domainConfig

	if (!domainConfig) return defaultRouteName

	for (const [, config] of Object.entries(domainConfig.pathnames)) {
		if (isAppAreaConfig(config) && config.funnel.slug === slug) {
			return config.paywall.routeName || defaultRouteName
		}
	}

	return defaultRouteName
}
